import { flatten } from 'lodash'
import dayjs, { Dayjs } from 'dayjs'
import { ContentType, Schedule } from 'hooks/queries/schedules/useAddToBrandPlanSchedule'
import { BrandPlanSchedule, SuggestionRequestStatus } from 'types'
import { MinimalMediaFile, platformManualSchedulingMapping } from '../../Types'
import { Additionals } from './Types'
import { sum } from 'lodash'

export const channelTypeToField: Record<ContentType, string> = {
  instagram_post: 'instagram_contents',
  instagram_story: 'instagram_contents',
  instagram_reel: 'instagram_contents',
  tiktok_post: 'tiktok_contents',
  youtube_short: 'youtube_contents',
  facebook_post: 'facebook_contents',
  linkedin_post: 'linkedin_contents',
  twitter_post: 'twitter_contents',
  twitter_thread: 'twitter_contents',
  blog_post: 'blog_contents',
  newsletter: 'newsletter_contents',
  crosspost_post: 'crosspost_contents',
  video_crosspost_post: 'video_crosspost_contents',
}

export const generateSchedule = (
  additionals: Additionals,
  topic: string,
  mediaFiles: MinimalMediaFile[],
  targetSchedule: BrandPlanSchedule,
  date?: Dayjs,
) => {
  const newSchedule = (Object.keys(additionals) as ContentType[]).map((key: ContentType) => {
    const num = additionals[key]

    const randomDay = (index: number) => {
      const startDate = dayjs(targetSchedule.start_date)
      const endDate = startDate.add(targetSchedule.length * 7, 'days')
      const today = dayjs()
      const tomorrow = today.add(1, 'day')
      const minDate = startDate.isAfter(tomorrow) ? startDate : tomorrow
      const effectiveStartDate = minDate.isAfter(endDate) ? endDate : minDate
      const daysDiff = endDate.diff(effectiveStartDate, 'day')
      const dayOffset = index % (daysDiff + 1)
      const postAt = effectiveStartDate.add(dayOffset, 'day')

      return postAt
    }

    return Array.from(
      { length: num },
      (_, index) =>
        ({
          type: key,
          topic: topic,
          post_at: date || randomDay(index),
          initial_media_file_ids: undefined,
        } as Schedule),
    )
  })

  const flattenedSchedule = flatten(newSchedule)
  flattenedSchedule.forEach((content, index) => {
    const mediaFile = mediaFiles[index]
    if (mediaFile) {
      content.initial_media_file_ids = [mediaFile.id]
    }
  })

  // Distribute times within platform-specific windows by type and weekday/weekend
  const postsByDateAndType = new Map<string, Schedule[]>()
  flattenedSchedule.forEach((content) => {
    const dateKey = content.post_at.format('YYYY-MM-DD')
    const key = `${dateKey}__${content.type}`
    const list = postsByDateAndType.get(key) || []
    list.push(content)
    postsByDateAndType.set(key, list)
  })

  postsByDateAndType.forEach((posts) => {
    const sample = posts[0]
    const isWeekend = [0, 6].includes(sample.post_at.day())
    const bestTimes = platformManualSchedulingMapping[sample.type]?.best_times_of_day_to_post
    const bestTimeToPost = isWeekend ? bestTimes?.weekends : bestTimes?.weekdays
    const window = bestTimeToPost ? bestTimeToPost : { from: 11, to: 15 }

    const fromHour = window.from
    const toHour = window.to
    const totalMinutes = Math.max((toHour - fromHour) * 60, 0)
    const count = posts.length

    if (count === 1) {
      const minutesFromStart = Math.round(totalMinutes / 2)
      const hour = fromHour + Math.floor(minutesFromStart / 60)
      const minute = minutesFromStart % 60
      posts[0].post_at = posts[0].post_at.hour(hour).minute(minute).second(0).millisecond(0)

      return
    }

    const stepMinutes = count > 1 ? totalMinutes / (count - 1) : 0
    posts.forEach((post, i) => {
      const minutesFromStart = Math.round(stepMinutes * i)
      const hour = fromHour + Math.floor(minutesFromStart / 60)
      const minute = minutesFromStart % 60
      post.post_at = post.post_at.hour(hour).minute(minute).second(0).millisecond(0)
    })
  })

  return flattenedSchedule
}

export const calculateChannelCounts = (targetSchedule?: BrandPlanSchedule) => {
  return targetSchedule?.contents.reduce((acc, content) => {
    if (content.is_crosspost) {
      if ([SuggestionRequestStatus.Failed, SuggestionRequestStatus.Expired].includes(content.status)) {
        return acc
      }
      acc['crosspost_post'] = (acc['crosspost_post'] || 0) + 1
      return acc
    }
    const contentType = content.type as ContentType
    if (!contentType || [SuggestionRequestStatus.Failed, SuggestionRequestStatus.Expired].includes(content.status))
      return acc
    acc[contentType] = (acc[contentType] || 0) + 1
    return acc
  }, {} as Record<ContentType, number>)
}

export const calculateTotalPostCount = (targetSchedule?: BrandPlanSchedule) => {
  const channelCounts = calculateChannelCounts(targetSchedule)
  return sum(Object.values(channelCounts || {}))
}

export const getCountFromDirectProperties = (channelType: ContentType, targetSchedule?: BrandPlanSchedule): number => {
  const fieldName = channelTypeToField[channelType]
  if (!fieldName || !targetSchedule) return 0

  // Map field names to actual schedule properties
  const fieldToProperty: Record<string, keyof BrandPlanSchedule> = {
    instagram_contents: 'instagram_contents',
    facebook_contents: 'facebook_contents',
    linkedin_contents: 'linkedin_contents',
    twitter_contents: 'twitter_contents',
    tiktok_contents: 'tiktok_contents',
    youtube_contents: 'youtube_contents',
    blog_contents: 'seo_contents',
    newsletter_contents: 'email_contents',
    crosspost_contents: 'crosspost_contents',
    video_crosspost_contents: 'video_crosspost_contents',
  }

  const propertyName = fieldToProperty[fieldName]
  if (!propertyName) return 0

  const contents = targetSchedule[propertyName] as Array<{ type: string; count: number }> | undefined
  if (!contents || !Array.isArray(contents)) return 0

  // For Instagram, match the specific type (post, story, reel)
  if (fieldName === 'instagram_contents') {
    const typeMap: Partial<Record<ContentType, string>> = {
      instagram_post: 'post',
      instagram_story: 'story',
      instagram_reel: 'reel',
    }
    const targetType = typeMap[channelType]
    if (!targetType) return 0
    const matchingEntry = contents.find((entry) => entry.type === targetType)
    return matchingEntry?.count || 0
  }

  // For other channels, sum all counts (usually just one entry)
  return contents.reduce((sum, entry) => sum + (entry.count || 0), 0)
}
